import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ICONS } from 'components/Icon';
import {
  ModalScreenProps,
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { useAlert } from 'hooks/useAlert';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { dateToNumber, randomKey } from 'lib/riskbase';
import { cloneDeep } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, NAVIGATE_MODAL_BACK } from 'store/actions';
import { doAseFieldResultsSave } from 'store/assessments/actions';
import {
  selectAseAssetId,
  selectAseTplFieldGroup,
  selectAseFieldResultsAse,
  selectAseFieldResultsAst,
  selectAseFieldResultsAstAse,
  selectAseId,
  selectAseIsApproved,
  selectAseIsEditable,
  selectAseIsOnline,
  selectAseTemplateFieldFieldsConfig,
  selectAseDataFieldFields,
  selectAseExplorerOptionShowValidationErrors,
} from 'store/assessments/selectors';
import { FieldTypes } from 'store/assets/types';
import { PinNew } from 'store/plan/types';
import { AppContextEnum, FieldResult } from 'types/types';
import { GlobalFieldInstanceScreen } from '../FieldComponents/GlobalFieldInstanceScreen';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_FIELDS
>;

export const FieldsScreenForNavigation: React.FC<Props> = ({
  navigation,
  route,
}) => <FieldsScreen route={route} navigation={navigation} />;

export const FieldsScreen: React.FC<
  | ModalScreenProps<ModalScreens.AseFields>
  | {
      route: Pick<Props['route'], 'params'>;
      navigation: {
        goBack: Props['navigation']['goBack'];
        setOptions: Props['navigation']['setOptions'];
      };
      isModal?: undefined;
    }
> = ({ navigation, route, isModal }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const alert = useAlert();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { groupId } = route.params;

  const assessmentId = useSelector(selectAseId);
  const isOnline = useSelector(selectAseIsOnline);
  const isApproved = useSelector(selectAseIsApproved);
  const isEditable = useSelector(selectAseIsEditable);
  const assetId = useSelector(selectAseAssetId);
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const fields = useSelector(selectAseDataFieldFields);
  const fieldConfigs = useSelector(selectAseTemplateFieldFieldsConfig);
  const showValidationErrors = useSelector(
    selectAseExplorerOptionShowValidationErrors,
  );

  const resultsMerged = useSelector(selectAseFieldResultsAstAse);
  const resultsAsset = useSelector(selectAseFieldResultsAst);
  const resultsAssessment = useSelector(selectAseFieldResultsAse);

  const dataRef = React.useRef(cloneDeep(resultsMerged));
  const resultsAssetRef = React.useRef(cloneDeep(resultsAsset));
  const resultsAssessmentRef = React.useRef(cloneDeep(resultsAssessment));

  const internalSave = !!isModal;
  const dirtyResultsRef = React.useRef<Record<string, FieldResult>>({});
  if (!internalSave) {
    dataRef.current = cloneDeep(resultsMerged);
  }

  const checkIsDirty = React.useCallback(
    () => !!Object.keys(dirtyResultsRef.current).length,
    [],
  );

  const handleSave = React.useCallback(() => {
    dispatch(
      doAseFieldResultsSave({
        results: { ...dirtyResultsRef.current },
      }),
    );
    if (!internalSave) {
      dirtyResultsRef.current = {};
    }
  }, [dispatch, internalSave]);

  const fieldKeys = React.useRef(
    (group?.fieldIds || []).reduce<Record<string, string>>((acc, fieldId) => {
      acc[fieldId] = `${fieldId}-${randomKey()}`;
      return acc;
    }, {}),
  );

  const handleChange = React.useCallback(
    (
      fieldId: string,
      value: string | string[] | null,
      pin?: PinNew | '' | null,
    ) => {
      const field = fields[fieldId];
      if (!field) {
        return;
      }
      let newResult: FieldResult | undefined;
      let removeResult: boolean = false;
      if (field.type === FieldTypes.PIN) {
        if (pin === null) {
          // Null pins remove the assessment's value and revert to the asset's value
          delete dataRef.current[fieldId];
          delete resultsAssessmentRef.current[fieldId];
          newResult = resultsAssetRef.current[fieldId];
          removeResult = true;
        } else {
          newResult = {
            fieldId,
            ts: dateToNumber(),
            value: '',
            pin: pin || '',
            action: null,
          };
          resultsAssessmentRef.current = {
            ...resultsAssessmentRef.current,
            [fieldId]: { ...newResult },
          };
        }
      } else if (value === null) {
        // Null values remove the assessment's value and revert to the asset's value
        delete dataRef.current[fieldId];
        delete resultsAssessmentRef.current[fieldId];
        newResult = resultsAssetRef.current[fieldId];
        removeResult = true;
      } else {
        newResult = {
          fieldId,
          ts: dateToNumber(),
          value: value || value === '0' ? value : '',
          pin: '',
          action: null,
        };
        resultsAssessmentRef.current = {
          ...resultsAssessmentRef.current,
          [fieldId]: { ...newResult },
        };
      }
      if (newResult) {
        dataRef.current = {
          ...dataRef.current,
          [fieldId]: { ...newResult },
        };
      }
      dirtyResultsRef.current[fieldId] = {
        fieldId,
        ts: dateToNumber(),
        value: value !== null ? value : '',
        pin: pin || '',
        action: removeResult ? 'delete' : null,
      };
      // Save right away
      if (isModal) {
        forceUpdate();
      } else {
        handleSave();
      }
    },
    [forceUpdate, fields, handleSave, isModal],
  );

  const handleRevert = React.useCallback(
    (fieldId: string) => {
      confirm({
        text: "This field has been changed in this assessment. Continuing will revert the value back to the asset's value",
        confirmText: 'Revert',
        destructive: true,
        onConfirm: () => {
          fieldKeys.current[fieldId] = `${fieldId}-${randomKey()}`;
          handleChange(fieldId, null, null);
        },
      });
    },
    [confirm, handleChange],
  );

  const subheading = isApproved ? undefined : 'Draft';
  const subheadingIcon = ICONS.CircleInfoLight;
  const onPressSubheading = () =>
    alert({
      text:
        'Changes will be applied to the asset when the assessment is approved\n\n' +
        'Until then, the asset will continue to appear in RiskBase with its existing details\n\n' +
        'Existing values are shown below changes',
    });

  const handleClose = React.useCallback(() => {
    if (isModal) {
      if (checkIsDirty()) {
        confirmUnsaved({
          onSave: handleSave,
          onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
        });
        return;
      }
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (screenSize === 'small') {
      navigation?.goBack();
    }
  }, [
    checkIsDirty,
    confirmUnsaved,
    dispatch,
    handleSave,
    isModal,
    navigation,
    screenSize,
  ]);

  const isDirty = checkIsDirty();
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [navigation, isDirty]);
  useEsc(isModal && handleClose);

  if (!group) {
    return null;
  }

  const { editableFieldIds, requiredFieldIds, hiddenFieldIds } =
    group.fieldIds.reduce(
      (acc, fieldId) => {
        const fieldConfig = fieldConfigs[fieldId];
        const field = fields[fieldId];
        if (!fieldConfig || !field) {
          acc.hiddenFieldIds.push(fieldId);
          return acc;
        }
        if (
          isEditable &&
          fieldConfig.edit &&
          (!isOnline ||
            (isModal &&
              ![
                FieldTypes.IMAGE,
                FieldTypes.FILE,
                FieldTypes.PIN,
                FieldTypes.GROUP,
                FieldTypes.GROUPLIGHT,
              ].includes(field.type)))
        ) {
          acc.editableFieldIds.push(fieldId);
        }
        if (fieldConfig.require) {
          acc.requiredFieldIds.push(fieldId);
        }
        return acc;
      },
      {
        editableFieldIds: [] as string[],
        requiredFieldIds: [] as string[],
        hiddenFieldIds: [] as string[],
      },
    );

  return (
    <GlobalFieldInstanceScreen
      context={{
        context: isOnline
          ? AppContextEnum.remoteAssessment
          : AppContextEnum.localAssessment,
        assetId,
        assessmentId,
        isModal,
      }}
      group={group}
      fields={fields}
      editableFieldIds={editableFieldIds}
      requiredFieldIds={requiredFieldIds}
      hiddenFieldIds={hiddenFieldIds}
      onEsc={handleClose}
      onChange={handleChange}
      onRevert={handleRevert}
      results={dataRef.current}
      resultsOld={resultsAssetRef.current}
      resultsNew={resultsAssessmentRef.current}
      fieldKeys={fieldKeys.current}
      // page props
      subheading={subheading}
      subheadingIcon={subheadingIcon}
      onPressSaveButton={
        isModal
          ? handleSave
          : isEditable && isOnline
            ? () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.AseFields,
                    params: {
                      groupId,
                    },
                  }),
                )
            : undefined
      }
      saveButtonText={isModal ? 'Save' : 'Edit'}
      onPressSubheading={onPressSubheading}
      onPressBackButton={
        !isModal && screenSize === ScreenSize.small ? handleClose : undefined
      }
      onPressCancelButton={isModal ? handleClose : undefined}
      showInvalid={showValidationErrors}
    />
  );
};
